import React, { useState, useEffect } from 'react';
import { Card, CardBody, Table, Badge, Input, Button } from 'reactstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CouponRedemptionsList = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    fetchRedemptions();
  }, [statusFilter, searchTerm, pagination.page]);

  const fetchRedemptions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/coupon/redemptions?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setRedemptions(response.data.redemptions || []);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      console.error('Error fetching redemptions:', err);
      setError('Failed to load redemptions');
      toast.error('Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      redeemed: { color: 'warning', text: 'Redeemed' },
      verified: { color: 'success', text: 'Verified' },
      expired: { color: 'secondary', text: 'Expired' }
    };
    const badge = badges[status] || { color: 'secondary', text: status };
    return <Badge color={badge.color}>{badge.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Card className="shadow-sm border-0">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">All Redemptions</h5>
          <div className="d-flex gap-2">
            <Input
              type="text"
              placeholder="Search code, email, name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ width: '250px' }}
            />
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{ width: '150px' }}
            >
              <option value="">All Status</option>
              <option value="redeemed">Redeemed</option>
              <option value="verified">Verified</option>
              <option value="expired">Expired</option>
            </Input>
            <Button color="secondary" onClick={fetchRedemptions}>
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : redemptions.length === 0 ? (
          <div className="text-center py-5 text-muted">
            No redemptions found
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Redeemed At</th>
                    <th>Verified At</th>
                    <th>Status</th>
                    <th>Banner</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((redemption) => (
                    <tr key={redemption.id}>
                      <td>
                        <strong style={{ 
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          letterSpacing: '0.2rem'
                        }}>
                          {redemption.couponCode}
                        </strong>
                      </td>
                      <td>{redemption.userName || 'N/A'}</td>
                      <td>{redemption.userEmail || 'N/A'}</td>
                      <td>{formatDate(redemption.redeemedAt)}</td>
                      <td>{formatDate(redemption.verifiedAt)}</td>
                      <td>{getStatusBadge(redemption.status)}</td>
                      <td>{redemption.bannerId || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} redemptions
                </div>
                <div>
                  <Button
                    color="secondary"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="mx-2">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    color="secondary"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </Card>
  );
};

export default CouponRedemptionsList;
